import { useCallback } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useWebContainer } from "@/features/web-container/context";
import { useParams } from "react-router-dom";
import { ReploError } from "schemas/errors";

import {
  useChatStoreActions,
  useIsChatLoading,
} from "../components/chat/store/chatStore";
import { useReactRenderStore } from "../store/reactRenderStore";
import { applyModifierChange } from "../utils/applyModifierChange";

// Error classes for modifier operations
class ModifierParameterError extends ReploError {}
class ModifierSelectedElementError extends ReploError {}
class ModifierWebContainerError extends ReploError {}

interface ModifierChanges {
  updatedText?: string;
  updatedClassName?: string;
}

export function useModifiers(): {
  applyChange: (changes: ModifierChanges) => Promise<void>;
  isLoading: boolean;
} {
  const projectId = useCurrentProjectId();
  const { pageId } = useParams<{ pageId: string }>();
  const { selectedElement } = useReactRenderStore();
  const isChatLoading = useIsChatLoading();
  const { setIsChatLoading } = useChatStoreActions();

  // Get WebContainer file system from context
  const { fs } = useWebContainer();

  const createOrUpdatePageComponent =
    trpc.nextJSProject.createOrUpdatePageComponent.useMutation();

  // Fetch the file content for the current page preemptively
  const { data: fileData } = trpc.nextJSProject.getPageComponent.useQuery({
    projectId: projectId!,
    id: pageId!,
  });

  const applyChange = useCallback(
    async (changes: ModifierChanges) => {
      if (!projectId || !pageId || !selectedElement || !fileData) {
        throw new ModifierParameterError({
          message: "Missing required parameters for modifier change",
          additionalData: {
            projectId,
            pageId,
            hasSelectedElement: Boolean(selectedElement),
          },
        });
      }

      // Validate selectedElement has required fields
      if (
        selectedElement.line === null ||
        selectedElement.column === null ||
        !selectedElement.file
      ) {
        throw new ModifierSelectedElementError({
          message: "Selected element is missing position or file information",
          additionalData: {
            line: selectedElement.line,
            column: selectedElement.column,
            hasFile: Boolean(selectedElement.file),
          },
        });
      }

      setIsChatLoading(true);

      await applyModifierChange({
        projectId,
        fileId: pageId,
        selectedElement: {
          props: selectedElement.props,
          file: selectedElement.file,
          line: selectedElement.line,
          column: selectedElement.column,
          childText: selectedElement.childText || "",
        },
        changes,
        fileContent: fileData.content,
        saveFile: async (content: string) => {
          await createOrUpdatePageComponent.mutateAsync({
            projectId,
            id: pageId,
            content,
            name: fileData.name,
          });
        },
        updateWebContainerFile: async (filePath: string, content: string) => {
          if (!fs) {
            throw new ModifierWebContainerError({
              message: "WebContainer filesystem not available",
              additionalData: { filePath },
            });
          }
          await fs.writeFile(filePath, content);
        },
      }).finally(() => {
        setIsChatLoading(false);
        // Invalidate and fetch latest content
        void trpcUtils.nextJSProject.getPageComponent.invalidate({
          projectId: projectId!,
          id: pageId!,
        });
      });
    },
    [
      projectId,
      pageId,
      selectedElement,
      createOrUpdatePageComponent,
      fs,
      setIsChatLoading,
      fileData,
    ],
  );

  return {
    applyChange,
    isLoading: isChatLoading,
  };
}
